import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { faThemeisle } from '@fortawesome/free-brands-svg-icons';
import { AuthService } from 'src/app/services/auth.service';


export type SudokuCell = {
    value: number | null;
    readonly: boolean;
    invalid?: boolean;
    focused?: boolean;
};

type TopScore = { name: string, score: number, created_at: string };

@Component({
    selector: 'app-sudoku',
    templateUrl: './sudoku.component.html',
    styleUrl: './sudoku.component.scss'
})
export class SudokuComponent implements OnInit {
    board: SudokuCell[][] = [];
    difficulty: 'easy' | 'medium' | 'hard' = 'medium';
    timer: number = 0; // in seconds
    intervalId: any = null;
    solved: boolean = false;
    selectedRow: number | null = null;
    selectedCol: number | null = null;
    topScores: TopScore[] = [];

    constructor(
        private httpClient: HttpClient,
        public authService: AuthService
    ) { };

    ngOnInit() {
        if (!this.loadFromLocalStorage()) {
            this.resetGame();
            this.startTimer();
        }
        else {
            this.isSolved();
        }
    }
  
    ngOnDestroy() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    get timerDisplay(): string {
        const minutes = Math.floor(this.timer / 60).toString().padStart(2, '0');
        const seconds = (this.timer % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    }

    generateFullBoard(): number[][] {
        const board = Array.from({ length: 9 }, () => Array(9).fill(0));
      
        const isSafe = (row: number, col: number, num: number): boolean => {
          for (let x = 0; x < 9; x++) {
            if (board[row][x] === num || board[x][col] === num) return false;
          }
          const startRow = row - row % 3;
          const startCol = col - col % 3;
          for (let i = 0; i < 3; i++) {
            for (let j = 0; j < 3; j++) {
              if (board[startRow + i][startCol + j] === num) return false;
            }
          }
          return true;
        };
      
        const fillBoard = (): boolean => {
          for (let row = 0; row < 9; row++) {
            for (let col = 0; col < 9; col++) {
              if (board[row][col] === 0) {
                const numbers = this.shuffle([...Array(9)].map((_, i) => i + 1));
                for (let num of numbers) {
                  if (isSafe(row, col, num)) {
                    board[row][col] = num;
                    if (fillBoard()) return true;
                    board[row][col] = 0;
                  }
                }
                return false;
              }
            }
          }
          return true;
        };
      
        fillBoard();
        return board;
    }

    shuffle(array: number[]): number[] {
        for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    getRemoveCountByDifficulty(): number {
        return this.difficulty === 'easy' ? 30 :
                this.difficulty === 'hard' ? 55 : 40;
    }

    generateRandomPuzzle(removeCount: number = 40) {
        const puzzle = this.generateFullBoard();

        let removed = 0;
        while (removed < removeCount) {
            const i = Math.floor(Math.random() * 9);
            const j = Math.floor(Math.random() * 9);
            if (puzzle[i][j] !== null) {
            puzzle[i][j] = null;
            removed++;
            }
        }

        this.board = puzzle.map(row =>
            row.map(cell => ({
            value: cell,
            readonly: cell !== null,
            invalid: false
            }))
        );

        this.saveToLocalStorage();
    }

    saveToLocalStorage() {
        const raw = {
            board: this.board.map(row =>
                row.map(cell => ({
                v: cell.value,
                r: cell.readonly
                }))
            ),
            timer: this.timer
        };
        localStorage.setItem('sudoku-board', JSON.stringify(raw));
    }
      
    loadFromLocalStorage(): boolean {
        const data = localStorage.getItem('sudoku-board');
        if (data) {
            try {
                const parsed = JSON.parse(data);
                this.timer = parsed.timer || 0;
                this.board = parsed.board.map((row: any[]) =>
                    row.map(cell => ({
                        value: cell.v,
                        readonly: cell.r,
                        invalid: false
                    }))
                );
                if (this.isSolved()) {
                    this.solved = true;
                }
                return true;
            } catch (e) {
                console.error('Failed to parse saved board.', e);
            }
        }
        return false;
    }

    resetGame() {
        this.generateRandomPuzzle(this.getRemoveCountByDifficulty());
        this.timer = 0;
        this.startTimer();
        this.solved = false;
    }

    updateCell(row: number, col: number, val: string) {
        const num = parseInt(val, 10);
        if (!this.board[row][col].readonly) {
          this.board[row][col].value = (!isNaN(num) && num >= 1 && num <= 9) ? num : null;
          this.validateBoard();
          this.saveToLocalStorage();
        }
    }

    handleKeyDown(event: KeyboardEvent, row: number, col: number) {
        const key = event.key;
    
        if (key === 'ArrowUp' && row > 0) this.focusCell(row - 1, col);
        else if (key === 'ArrowDown' && row < 8) this.focusCell(row + 1, col);
        else if (key === 'ArrowLeft' && col > 0) this.focusCell(row, col - 1);
        else if (key === 'ArrowRight' && col < 8) this.focusCell(row, col + 1);
    }

    focusCell(row: number, col: number) {
        this.selectedRow = row;
        this.selectedCol = col;

        const id = `cell-${row}-${col}`;
        const el = document.getElementById(id);
        el?.focus();
    }

    onCellSelect(row: number, col: number) {
        this.selectedRow = row;
        this.selectedCol = col;
    }


    selectNumber(n: number) {
        if (this.selectedRow !== null && this.selectedCol !== null) {
          const cell = this.board[this.selectedRow][this.selectedCol];
          if (!cell.readonly) {
            cell.value = n;
            this.validateBoard(); // Optional: validate after inserting
          }
        }
        this.focusCell(this.selectedRow, this.selectedCol);
    }

    clearCell() {
        if (this.selectedRow !== null && this.selectedCol !== null) {
            const cell = this.board[this.selectedRow][this.selectedCol];
            if (!cell.readonly) {
                cell.value = null;
                this.validateBoard(); // Optional
            }
        }
    }

    validateBoard() {
        if (!this.solved)
        {        
            // Clear all invalid flags
            for (let i = 0; i < 9; i++) {
                for (let j = 0; j < 9; j++) {
                    this.board[i][j].invalid = false;
                }
            }
        
            const markConflicts = (positions: [number, number][]) => {
                const seen = new Map<number, [number, number][]>();
            
                for (const [i, j] of positions) {
                    const val = this.board[i][j].value;
                    if (val !== null) {
                        if (!seen.has(val)) {
                            seen.set(val, []);
                        }
                    seen.get(val)!.push([i, j]);
                    }
                }
            
                for (const entries of seen.values()) {
                    if (entries.length > 1) {
                        for (const [i, j] of entries) {
                            this.board[i][j].invalid = true;
                        }
                    }
                }
            };
        
            // Check rows
            for (let i = 0; i < 9; i++) {
                markConflicts([...Array(9)].map((_, j) => [i, j]));
            }
        
            // Check columns
            for (let j = 0; j < 9; j++) {
                markConflicts([...Array(9)].map((_, i) => [i, j]));
            }
        
            // Check 3x3 boxes
            for (let boxRow = 0; boxRow < 3; boxRow++) {
                for (let boxCol = 0; boxCol < 3; boxCol++) {
                    const cells: [number, number][] = [];
                    for (let i = 0; i < 3; i++) {
                        for (let j = 0; j < 3; j++) {
                            cells.push([boxRow * 3 + i, boxCol * 3 + j]);
                        }
                    }
                    markConflicts(cells);
                }
            }
            if (this.isSolved())
            {
                this.saveScore();
                this.solved = true;
            }
        }
    }

    isSolved(): boolean {
        for (let i = 0; i < 9; i++) {
          for (let j = 0; j < 9; j++) {
            const cell = this.board[i][j];
            if (cell.value === null || cell.invalid) return false;
          }
        }
        this.solved = true;
        clearInterval(this.intervalId);
        this.getTopScores();
        return true;
    }

    handleFocus(event: FocusEvent, row: number, col: number) {
        // Clear previous focused states
        for (let i = 0; i < 9; i++) {
            for (let j = 0; j < 9; j++) {
            this.board[i][j].focused = false;
            }
        }

        // Set the focused state for the currently selected cell
        this.board[row][col].focused = true;

        const input = event.target as HTMLInputElement;
        if (!input.readOnly) {
            setTimeout(() => input.select(), 0);
        }
    }

    isHighlighted(cell: SudokuCell): boolean {
        const focusedCell = this.board.flat().find(c => c.focused);
        return (
            focusedCell &&
            cell.value !== null &&
            cell.value === focusedCell.value &&
            !cell.focused // optional: don't highlight the currently focused cell
        );
    }


    startTimer() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.intervalId = setInterval(() => {
            this.timer++;
        }, 1000);
    }

    saveScore() {
        if (this.authService.loggedIn()) {   
          const token = this.authService.getToken(); 
          this.httpClient.post(
            `https://ng.renner.se/games.api.php?action=save_score`,
            { 
              game_id: 2,
              score: this.timer,
              difficulty: this.difficulty 
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          ).subscribe({
            next: (response) => {
              console.log('Score saved successfully:', response);
            },
            error: (err) => {
              console.error('Failed to save score:', err);
            }
          });
        }
    }

    getTopScores() {
        let token = this.authService.getToken();
        return this.httpClient.get<TopScore[]>(
            `https://ng.renner.se/games.api.php?action=get_top_scores`,
            {
                params: {
                    game_id: 2,
                    difficulty: this.difficulty,
                    order: 'ASC',
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        ).subscribe({
            next: (scores) => {
              console.log('Scores: ', scores);
              this.topScores = scores;
            },
            error: (err) => {
              console.error('Failed to get top score:', err);
            }

        });
    }

}
